import React from 'react';
import { X, Users, UserCheck, Building, Mail, Phone } from 'lucide-react';
import { useUsers } from '../contexts/UserContext';

const AssignedUsersModal = ({ isOpen, onClose, assignments, title = "Assigned Users" }) => {
  const { users } = useUsers();

  if (!isOpen) return null;

  const getAssignedUsers = () => {
    const assignedUsers = new Set();
    const assignmentsData = assignments || { 
      roles: [], 
      users: [], 
      departments: [], 
      designations: [], 
      specializations: [] 
    };

    // Get users assigned by individual email
    if (assignmentsData.users && Array.isArray(assignmentsData.users)) {
      assignmentsData.users.forEach(userEmail => {
        const user = users.find(u => u.email === userEmail);
        if (user) {
          assignedUsers.add(user);
        }
      });
    }

    // Get users assigned by role (use synchronous filter from existing users array)
    if (assignmentsData.roles && Array.isArray(assignmentsData.roles)) {
      assignmentsData.roles.forEach(role => {
        const roleUsers = users.filter(user => user.role === role && user.isActive);
        roleUsers.forEach(user => assignedUsers.add(user));
      });
    }

    // Get users assigned by department (use synchronous filter from existing users array)
    if (assignmentsData.departments && Array.isArray(assignmentsData.departments)) {
      assignmentsData.departments.forEach(department => {
        const deptUsers = users.filter(user => user.department === department && user.isActive);
        deptUsers.forEach(user => assignedUsers.add(user));
      });
    }

    // Get users assigned by designation
    if (assignmentsData.designations && Array.isArray(assignmentsData.designations)) {
      assignmentsData.designations.forEach(designation => {
        const designationUsers = users.filter(user => user.designation === designation && user.isActive);
        designationUsers.forEach(user => assignedUsers.add(user));
      });
    }

    // Get users assigned by specialization
    if (assignmentsData.specializations && Array.isArray(assignmentsData.specializations)) {
      assignmentsData.specializations.forEach(specialization => {
        const specializationUsers = users.filter(user => user.specialization === specialization && user.isActive);
        specializationUsers.forEach(user => assignedUsers.add(user));
      });
    }

    return Array.from(assignedUsers);
  };

  const assignedUsers = getAssignedUsers();

  const getAssignmentReason = (user) => {
    const reasons = [];
    const assignmentsData = assignments || { 
      roles: [], 
      users: [], 
      departments: [], 
      designations: [], 
      specializations: [] 
    };

    if (assignmentsData.users && assignmentsData.users.includes(user.email)) {
      reasons.push('Direct Assignment');
    }
    if (assignmentsData.roles && assignmentsData.roles.includes(user.role)) {
      reasons.push(`Role: ${user.role}`);
    }
    if (assignmentsData.departments && assignmentsData.departments.includes(user.department)) {
      reasons.push(`Department: ${user.department}`);
    }
    if (assignmentsData.designations && assignmentsData.designations.includes(user.designation)) {
      reasons.push(`Designation: ${user.designation}`);
    }
    if (assignmentsData.specializations && assignmentsData.specializations.includes(user.specialization)) {
      reasons.push(`Specialization: ${user.specialization}`);
    }

    return reasons.join(', ');
  };

  return (
    <div className="modal-overlay">
      <div className="modal assigned-users-modal">
        <div className="modal-header">
          <div className="modal-title">
            <Users size={20} style={{ marginRight: '8px' }} />
            {title}
          </div>
          <button onClick={onClose} className="btn-icon">
            <X size={16} />
          </button>
        </div>
        
        <div className="modal-content">
          {assignedUsers.length > 0 ? (
            <div className="assigned-users-list">
              <div className="users-summary mb-4">
                <p className="text-gray-600">
                  <strong>{assignedUsers.length}</strong> user(s) assigned to this training
                </p>
              </div>
              
              <div className="users-grid">
                {assignedUsers.map((user) => (
                  <div key={user.id} className="user-card">
                    <div className="user-avatar">
                      <UserCheck size={24} color="#1e40af" />
                    </div>
                    <div className="user-info">
                      <h4 className="user-name">{user.name}</h4>
                      <p className="user-email">
                        <Mail size={14} style={{ marginRight: '4px' }} />
                        {user.email}
                      </p>
                      <p className="user-role">
                        <Building size={14} style={{ marginRight: '4px' }} />
                        {user.role} • {user.department}
                      </p>
                      <p className="assignment-reason">
                        <span className="reason-label">Assigned via:</span>
                        <span className="reason-value">{getAssignmentReason(user)}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-assignments">
              <Users size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Users Assigned</h3>
              <p className="text-gray-500">This training is not assigned to any users yet.</p>
            </div>
          )}
        </div>
        
        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignedUsersModal;
